import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'
import * as BaseEventTicketsActions from './base-event-tickets.actions'
import { HttpErrorResponse } from '@angular/common/http'
import {
  BaseEventTicketEntity,
  EVENT_GUESTS_DEFAULT_LIMIT,
  EVENT_GUESTS_DEFAULT_OFFSET,
  EventGuestSummary,
  EventTicketFilters,
} from './base-event-tickets.models'
import { selectAll } from './base-event-tickets.selectors'

export const EVENT_TICKETS_FEATURE_KEY = 'eventTickets'

export interface EventTicketsState extends EntityState<BaseEventTicketEntity> {
  selectedId?: string | number
  loaded: boolean
  count: number
  error?: HttpErrorResponse | null
  summary?: EventGuestSummary
  limit?: number
  offset?: number
  filters?: EventTicketFilters
}

export interface EventTicketsPartialState {
  readonly [EVENT_TICKETS_FEATURE_KEY]: EventTicketsState
}

export const eventTicketsAdapter: EntityAdapter<BaseEventTicketEntity> = createEntityAdapter<BaseEventTicketEntity>({
  selectId: (eventTicket: BaseEventTicketEntity) => eventTicket.uuid,
})

export const initialEventTicketsState: EventTicketsState = eventTicketsAdapter.getInitialState({
  loaded: false,
  count: 0,
})

const eventTicketsReducer = createReducer(
  initialEventTicketsState,
  on(BaseEventTicketsActions.loadEventTickets, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(BaseEventTicketsActions.loadFilteredEventTickets, (state, action) => ({
    ...state,
    filters: action.filters,
    limit: state.limit ?? EVENT_GUESTS_DEFAULT_LIMIT,
    offset: state.offset ?? EVENT_GUESTS_DEFAULT_OFFSET,
  })),
  on(BaseEventTicketsActions.loadEventTicketsSuccess, (state, action) => {
    const notListLoadedTickets = [...selectAll(state)].filter(
      (ticket) => ticket.notListLoaded && !action.eventTickets.some((t) => t.uuid === ticket.uuid),
    )

    return eventTicketsAdapter.setAll([...action.eventTickets, ...notListLoadedTickets], {
      ...state,
      loaded: true,
      count: action.count,
    })
  }),
  on(BaseEventTicketsActions.loadEventTicketsFailure, (state) => ({
    ...state,
    loaded: true,
  })),
  on(BaseEventTicketsActions.loadEventGuestSummarySuccess, (state, { summary }) => ({ ...state, summary })),
  on(BaseEventTicketsActions.updateEventGuestSummaryRealtime, (state, action) => ({
    ...state,
    summary: action.update.changes as EventGuestSummary,
  })),
  on(BaseEventTicketsActions.upsertEventTicket, (state, { eventTicket }) =>
    eventTicketsAdapter.upsertOne(eventTicket, state),
  ),
  on(BaseEventTicketsActions.setPaginationSettings, (state, { paginationPreferences }) => ({
    ...state,
    ...paginationPreferences,
  })),
  on(
    BaseEventTicketsActions.updateSelectedEventTicket,
    // update selected event ticket on action or dialog open
    BaseEventTicketsActions.startCheckInProcess,
    BaseEventTicketsActions.openEventTicketAssignDialog,
    BaseEventTicketsActions.openEventTicketEditGuestDialog,
    BaseEventTicketsActions.openEventTicketTransferDialog,
    BaseEventTicketsActions.openResendInviteDialog,
    BaseEventTicketsActions.openResendTicketDialog,
    (state, { uuid }) => ({ ...state, selectedId: uuid }),
  ),
  on(BaseEventTicketsActions.clearSelectedEventTicket, (state) => ({ ...state, selectedId: '' })),
  on(BaseEventTicketsActions.removeEventTicket, (state, action) => eventTicketsAdapter.removeOne(action.uuid, state)),
  on(BaseEventTicketsActions.removeManyEventTickets, (state, action) =>
    eventTicketsAdapter.removeMany(action.uuids, state),
  ),
  on(BaseEventTicketsActions.removeAllEventTickets, (state) => eventTicketsAdapter.removeAll(state)),
  on(BaseEventTicketsActions.clearEventTickets, () => ({
    ...initialEventTicketsState,
  })),
)

export function reducer(state: EventTicketsState | undefined, action: Action) {
  return eventTicketsReducer(state, action)
}
